import type {
  WrongRecipientAppointmentPreview,
  WrongRecipientContactPreview,
  WrongRecipientPreviewPayload,
} from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown, path: string): JsonObject => {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error(`${path} muss ein Objekt sein.`)

  return value as JsonObject
}

const exactKeys = (value: JsonObject, allowed: readonly string[], path: string) => {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key))

  if (unexpected.length)
    throw new Error(`${path} enthält unbekannte Felder: ${unexpected.join(", ")}.`)
}

const requiredString = (value: unknown, path: string): string => {
  if (typeof value !== "string" || !value.trim())
    throw new Error(`${path} muss ein nicht-leerer String sein.`)

  return value.trim()
}

const optionalString = (value: unknown, path: string): string => {
  if (value === null || value === undefined) return ""

  if (typeof value !== "string") throw new Error(`${path} muss String oder null sein.`)

  return value.trim()
}

const appointmentValue = (value: unknown, path: string): WrongRecipientAppointmentPreview => {
  const item = objectValue(value, path)
  exactKeys(
    item,
    [
      "schedule_event_id",
      "start_at",
      "end_at",
      "status",
      "communication_presentation",
      "employee_name",
      "flex_type",
      "timeframe_start_at",
      "timeframe_end_at",
      "customer_window_start_at",
      "customer_window_end_at",
    ],
    path,
  )

  return {
    scheduleEventId: requiredString(item.schedule_event_id, `${path}.schedule_event_id`),
    startAt: optionalString(item.start_at, `${path}.start_at`),
    endAt: optionalString(item.end_at, `${path}.end_at`),
    status: requiredString(item.status, `${path}.status`),
    employeeName: optionalString(item.employee_name, `${path}.employee_name`),
    communicationPresentation:
      item.communication_presentation as WrongRecipientAppointmentPreview["communicationPresentation"],
    flexType: optionalString(item.flex_type, `${path}.flex_type`),
    timeframeStartAt: optionalString(item.timeframe_start_at, `${path}.timeframe_start_at`),
    timeframeEndAt: optionalString(item.timeframe_end_at, `${path}.timeframe_end_at`),
    customerWindowStartAt: optionalString(
      item.customer_window_start_at,
      `${path}.customer_window_start_at`,
    ),
    customerWindowEndAt: optionalString(
      item.customer_window_end_at,
      `${path}.customer_window_end_at`,
    ),
  }
}

const contactValue = (value: unknown, path: string): WrongRecipientContactPreview => {
  const item = objectValue(value, path)
  exactKeys(item, ["customer_id", "display_name", "email", "phone", "role"], path)

  return {
    customerId: requiredString(item.customer_id, `${path}.customer_id`),
    displayName: optionalString(item.display_name, `${path}.display_name`),
    email: optionalString(item.email, `${path}.email`),
    phone: optionalString(item.phone, `${path}.phone`),
    role: optionalString(item.role, `${path}.role`),
  }
}

export const parseWrongRecipientPreview = (payload: unknown): WrongRecipientPreviewPayload => {
  const root = objectValue(payload, "payload")
  exactKeys(
    root,
    ["row_context_by_action_case_id", "row_errors_by_action_case_id", "shared"],
    "payload",
  )
  const rawRows = objectValue(
    root.row_context_by_action_case_id,
    "payload.row_context_by_action_case_id",
  )
  const rawErrors = objectValue(
    root.row_errors_by_action_case_id,
    "payload.row_errors_by_action_case_id",
  )
  const shared = objectValue(root.shared, "payload.shared")
  exactKeys(shared, ["appointments_by_id"], "payload.shared")
  const rawAppointments = objectValue(
    shared.appointments_by_id,
    "payload.shared.appointments_by_id",
  )

  const appointmentsById = Object.fromEntries(
    Object.entries(rawAppointments).map(([id, value]) => {
      const appointment = appointmentValue(value, `payload.shared.appointments_by_id.${id}`)

      if (appointment.scheduleEventId !== id)
        throw new Error(`Termin ${id} referenziert eine abweichende schedule_event_id.`)

      return [id, appointment]
    }),
  )
  const rowContextByActionCaseId = Object.fromEntries(
    Object.entries(rawRows).map(([actionCaseId, value]) => {
      const row = objectValue(value, `payload.row_context_by_action_case_id.${actionCaseId}`)
      exactKeys(
        row,
        ["work_order_id", "recipient_customer_id", "recipient_contact", "appointment_id"],
        `payload.row_context_by_action_case_id.${actionCaseId}`,
      )

      return [
        actionCaseId,
        {
          workOrderId: requiredString(
            row.work_order_id,
            `payload.row_context_by_action_case_id.${actionCaseId}.work_order_id`,
          ),
          recipientCustomerId: requiredString(
            row.recipient_customer_id,
            `payload.row_context_by_action_case_id.${actionCaseId}.recipient_customer_id`,
          ),
          recipientContact: contactValue(
            row.recipient_contact,
            `payload.row_context_by_action_case_id.${actionCaseId}.recipient_contact`,
          ),
          appointmentId:
            optionalString(
              row.appointment_id,
              `payload.row_context_by_action_case_id.${actionCaseId}.appointment_id`,
            ) || null,
        },
      ]
    }),
  )
  const rowErrorsByActionCaseId = Object.fromEntries(
    Object.entries(rawErrors).map(([id, value]) => [
      id,
      requiredString(value, `payload.row_errors_by_action_case_id.${id}`),
    ]),
  )

  for (const [actionCaseId, row] of Object.entries(rowContextByActionCaseId)) {
    if (row.recipientContact.customerId !== row.recipientCustomerId) {
      throw new Error(
        `ActionCase ${actionCaseId} enthält einen Empfänger-Kontakt mit abweichender customer_id.`,
      )
    }

    if (row.appointmentId && !appointmentsById[row.appointmentId]) {
      throw new Error(
        `ActionCase ${actionCaseId} referenziert den unbekannten Termin ${row.appointmentId}.`,
      )
    }
  }

  return { rowContextByActionCaseId, rowErrorsByActionCaseId, appointmentsById }
}
